import { Chat, ChatContext, ChatSidebar } from "@/components/chat/chat";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useChatChannel } from "@/hooks/use-chat-channel";
import { cn } from "@/lib/utils";
import { Chat as ChatType, ChatWithMessagesType, Message, StatusUpdate } from "@/types/chat";
import { WhatsappStatus } from "@/types/whatsapp";
import { TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const EXPERIMENTAL_DISMISSED_KEY = "chats-experimental-dismissed";

type ChatWorkspaceProps = {
  chats: ChatType[];
  status: WhatsappStatus;
  selectedChat?: ChatWithMessagesType;
  canSendMessage?: boolean;
  canMarkSale?: boolean;
  saleMarkedInLast24h?: boolean;
  archivedMode?: boolean;
  isArchived?: boolean;
};

export function ChatWorkspace({
  chats: initialChats,
  status,
  selectedChat: initialSelectedChat,
  canSendMessage = true,
  canMarkSale = false,
  saleMarkedInLast24h = false,
  archivedMode = false,
  isArchived = false,
}: ChatWorkspaceProps) {
  const [chats, setChats] = useState(initialChats);
  const [selectedChat, setSelectedChat] = useState(initialSelectedChat ?? null);
  const [showExperimentalAlert, setShowExperimentalAlert] = useState(
    () => !localStorage.getItem(EXPERIMENTAL_DISMISSED_KEY),
  );

  function dismissExperimentalAlert() {
    localStorage.setItem(EXPERIMENTAL_DISMISSED_KEY, "true");
    setShowExperimentalAlert(false);
  }

  useEffect(() => {
    setChats(initialChats);
  }, [initialChats]);

  useEffect(() => {
    setSelectedChat(initialSelectedChat ?? null);
  }, [initialSelectedChat]);

  const markReadRef = useRef<((remoteJid: string) => void) | null>(null);

  const handleMessage = useCallback(
    (data: { chatRemoteJid: string; message: Message }) => {
      if (selectedChat?.remoteJid === data.chatRemoteJid) {
        setSelectedChat((prev) => {
          if (!prev) return prev;
          const exists = prev.messages.some((m) => m.id === data.message.id);
          if (exists) return prev;
          return { ...prev, messages: [...prev.messages, data.message] };
        });

        if (!data.message.self) {
          markReadRef.current?.(data.chatRemoteJid);
        }
      }

      setChats((prev) =>
        prev.map((chat) =>
          chat.remoteJid === data.chatRemoteJid
            ? {
                ...chat,
                lastMessage: data.message,
                unreadCount:
                  chat.remoteJid === selectedChat?.remoteJid
                    ? chat.unreadCount
                    : chat.unreadCount + 1,
              }
            : chat,
        ),
      );
    },
    [selectedChat?.remoteJid],
  );

  const handleError = useCallback((error: string) => {
    toast.error(error);
  }, []);

  const handleStatusUpdate = useCallback(
    (data: { chatRemoteJid: string; statusUpdate: StatusUpdate }) => {
      const { chatRemoteJid, statusUpdate } = data;

      if (selectedChat?.remoteJid === chatRemoteJid) {
        setSelectedChat((prev) => {
          if (!prev) return prev;
          const updated = prev.messages.map((m) =>
            String(m.id) === statusUpdate.keyId
              ? { ...m, status: statusUpdate.status }
              : m,
          );
          if (updated === prev.messages) return prev;
          return { ...prev, messages: updated };
        });
      }

      setChats((prev) =>
        prev.map((chat) => {
          if (chat.remoteJid !== chatRemoteJid) return chat;
          if (!chat.lastMessage) return chat;
          if (String(chat.lastMessage.id) !== statusUpdate.keyId) return chat;
          return {
            ...chat,
            lastMessage: { ...chat.lastMessage, status: statusUpdate.status },
          };
        }),
      );
    },
    [selectedChat?.remoteJid],
  );

  const { sendMessage, markRead } = useChatChannel({
    onMessage: handleMessage,
    onStatusUpdate: handleStatusUpdate,
    onError: handleError,
  });
  markReadRef.current = markRead;

  useEffect(() => {
    if (!selectedChat) return;
    markRead(selectedChat.remoteJid);
  }, [selectedChat?.remoteJid, markRead]);

  if (status.status === "error") {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3">
        <TriangleAlert className="size-10 text-destructive" />
        <p className="font-medium text-foreground">
          Falha na conexão com o servidor do WhatsApp
        </p>
        <p className="text-sm text-center max-w-md">
          Não foi possível conectar ao serviço do WhatsApp. Verifique se o
          servidor está em execução e tente novamente.
        </p>
      </div>
    );
  }

  if (status.status !== "connected") {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <p>WhatsApp não está conectado.</p>

        <p>
          Conecte-se através da página de configurações para começar a
          conversar.{" "}
        </p>
        <p>
          Ou clique{" "}
          <a href="/app/settings?tab=whatsapp" className="underline">
            aqui
          </a>{" "}
          para ir para as configurações.
        </p>
      </div>
    );
  }

  return (
    <ChatContext.Provider
      value={{ selectedChat, chats, canSendMessage, canMarkSale, saleMarkedInLast24h, archivedMode, isArchived, sendMessage }}
    >
      <AlertDialog open={showExperimentalAlert} onOpenChange={setShowExperimentalAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Funcionalidade Experimental</AlertDialogTitle>
            <AlertDialogDescription>
              A área de conversas é uma funcionalidade experimental. Você pode
              encontrar instabilidades ou comportamentos inesperados durante o
              uso. Estamos trabalhando continuamente para melhorar a experiência.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={dismissExperimentalAlert}>
              Entendido
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex flex-1 min-h-0 min-w-0 gap-4">
        <ChatSidebar
          className={cn(
            "max-md:w-full max-md:shrink",
            selectedChat && "max-md:hidden",
          )}
        />
        <div
          className={cn(
            "flex flex-1 min-h-0 min-w-0",
            !selectedChat && "max-md:hidden",
          )}
        >
          <Chat />
        </div>
      </div>
    </ChatContext.Provider>
  );
}
